import { useState } from "react";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { RolesEnum } from "@/lib/types/roles.types";
import { Json } from "@/lib/supabase/supabaseTypesImport";
import { getReminderStageLabel } from "@/lib/utils/reminder-stage-label";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import {
	getCurrencyFromRegion,
	useRegionKind,
} from "../../lib/utils/region-utils";
import { formatCurrency } from "../../lib/utils/utils-functions";
import { applyRounding } from "../../lib/utils/calculate";
import {
	buildReminderTimeline,
	getReminderStageFee,
} from "../../pages/debitoren/mahnwesen/utils/mahnwesen-timeline";
import {
	FIRST_FEE_STAGE,
	MAX_REMINDER_STAGE,
} from "../../pages/debitoren/mahnwesen/utils/reminder-stage";
import { useCentralStore } from "../../store/Central";
import { useRemindersStore } from "../../store/Reminders";
import { showNotification } from "../../store/Central/selectors";
import { Region } from "../../types/enums";

const DAY_FIELDS = [
	{ key: "days_payment", label: "Zahlungsfrist Rechnung (Tage)" },
	{
		key: "days_payment_akonto",
		label: "Zahlungsfrist Akontorechnung (Tage)",
	},
	{
		key: "days_payment_reminder",
		label: "Wartezeit bis Zahlungserinnerung (Tage nach Fälligkeit)",
	},
	{
		key: "days_payment_mahnung_1",
		label: "Wartezeit bis 1. Mahnung (Tage)",
	},
	{
		key: "days_payment_mahnung_2",
		label: "Wartezeit bis Folgemahnung (Tage)",
	},
] as const;

const DUE_BASIS_OPTIONS = [
	{ value: "invoice_date", label: "Rechnungsdatum" },
	{ value: "created_at", label: "Erstellungsdatum der Rechnung" },
] as const;

const USUAL_MAX_FEE = 40;

type DayFieldKey = (typeof DAY_FIELDS)[number]["key"];

interface ScheduleValues extends Record<DayFieldKey, number> {
	mahnung_max_count: number;
	fees: number[];
}

interface SettingsUpdate extends Partial<Record<DayFieldKey, number>> {
	mahnung_max_count?: number;
	reminder_fee_amounts?: Json;
	reminder_due_basis?: string;
	show_reminder_sidebar_badge?: boolean;
}

const toFeeAmounts = (fees: number[]): Json =>
	Object.fromEntries(
		fees.flatMap((fee, index) =>
			fee > 0 && index + 1 >= FIRST_FEE_STAGE
				? [[`stage_${index + 1}`, fee]]
				: []
		)
	);

const isValidAmount = (value: number) => Number.isFinite(value) && value >= 0;

const MAX_DAYS = 32767;

const isValidDays = (value: number) =>
	Number.isInteger(value) && value >= 0 && value <= MAX_DAYS;

const parseAmount = (raw: string) => (raw.trim() === "" ? NaN : Number(raw));

const toInputValue = (value: number) => (Number.isNaN(value) ? "" : value);

export const CompanyMahnungswesenInformation: React.FC = () => {
	const organization = useCentralStore((state) => state.organization);
	const role = useCentralStore((state) => state.role);
	const activatedRegions = useCentralStore((state) => state.activatedRegions);
	const updateOrganizationBatch = useCentralStore(
		(state) => state.updateOrganizationBatch
	);
	const region = useRegionKind(null);
	const currency = organization
		? getCurrencyFromRegion(null, organization, activatedRegions)
		: "CHF";
	const [draft, setDraft] = useState<Partial<ScheduleValues>>({});
	const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">(
		"idle"
	);
	const isOwner = role === RolesEnum.OWNER;
	const dueBasis = organization?.reminder_due_basis ?? "invoice_date";

	const savedValues: ScheduleValues = {
		days_payment: organization?.days_payment ?? 30,
		days_payment_akonto: organization?.days_payment_akonto ?? 30,
		days_payment_reminder: organization?.days_payment_reminder ?? 0,
		days_payment_mahnung_1: organization?.days_payment_mahnung_1 ?? 0,
		days_payment_mahnung_2: organization?.days_payment_mahnung_2 ?? 0,
		mahnung_max_count: organization?.mahnung_max_count ?? 1,
		fees: Array.from({ length: MAX_REMINDER_STAGE }, (_, index) =>
			getReminderStageFee(
				organization?.reminder_fee_amounts ?? null,
				index + 1
			)
		),
	};
	const values: ScheduleValues = { ...savedValues, ...draft };

	const timeline = buildReminderTimeline({
		...values,
		...Object.fromEntries(
			DAY_FIELDS.map(({ key }) => [
				key,
				isValidDays(values[key]) ? values[key] : savedValues[key],
			])
		),
		reminder_fee_amounts: toFeeAmounts(
			values.fees.map((fee) => (isValidAmount(fee) ? fee : 0))
		),
	});

	// Only drops draft values that are still what was saved, so typing during a save survives.
	const save = async (
		update: SettingsUpdate,
		savedDraft: Partial<ScheduleValues> = {}
	) => {
		setSaveState("saving");
		const { error } = await updateOrganizationBatch(update, true);
		if (error) {
			setSaveState("idle");
			return;
		}
		useRemindersStore.getState().fetchDueCount();
		setDraft((current) => {
			const next = { ...current };
			(Object.keys(savedDraft) as (keyof ScheduleValues)[]).forEach(
				(key) => {
					if (
						JSON.stringify(current[key]) ===
						JSON.stringify(savedDraft[key])
					) {
						delete next[key];
					}
				}
			);
			return next;
		});
		setSaveState("saved");
	};

	const handleDayBlur = (key: DayFieldKey) => {
		if (draft[key] === undefined || draft[key] === savedValues[key]) return;
		if (!isValidDays(values[key])) {
			showNotification({
				message: `Bitte eine ganze Zahl von 0 bis ${MAX_DAYS} eingeben.`,
				type: "error",
			});
			return;
		}
		save({ [key]: values[key] }, { [key]: values[key] });
	};

	const handleFeeBlur = () => {
		if (!draft.fees) return;
		if (!values.fees.every(isValidAmount)) {
			showNotification({
				message: "Bitte nur positive Beträge eingeben.",
				type: "error",
			});
			return;
		}
		save(
			{
				reminder_fee_amounts: toFeeAmounts(
					values.fees.map((fee) => applyRounding(fee))
				),
			},
			{ fees: values.fees }
		);
	};

	const handleFeeChange = (index: number, fee: number) =>
		setDraft((current) => ({
			...current,
			fees: values.fees.map((value, feeIndex) =>
				feeIndex === index ? fee : value
			),
		}));

	return (
		<div
			className="flex flex-col gap-8"
			data-testid="mahnungswesen-settings"
		>
			{isOwner ? (
				<p
					className="text-sm text-gray-500"
					data-testid="mahnungswesen-save-state"
				>
					{saveState === "saving"
						? "Speichert …"
						: saveState === "saved"
							? "Gespeichert"
							: "Änderungen werden automatisch gespeichert."}
				</p>
			) : (
				<div
					className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900"
					data-testid="mahnungswesen-readonly-hint"
				>
					<Info className="h-4 w-4 shrink-0" />
					Nur der Inhaber des Labors kann das Mahnungswesen
					bearbeiten.
				</div>
			)}

			<section className="grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-3">
				<div className="flex flex-col gap-4">
					<h2 className="text-lg font-semibold">Fristen</h2>
					<div className="flex flex-col gap-1.5">
						<Label>Zahlungsfrist läuft ab</Label>
						<Select
							value={dueBasis}
							onValueChange={(value) =>
								save({ reminder_due_basis: value })
							}
							disabled={!isOwner}
						>
							<SelectTrigger
								aria-label="Zahlungsfrist läuft ab"
								data-testid="mahnungswesen-due-basis"
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{DUE_BASIS_OPTIONS.map((option) => (
									<SelectItem
										key={option.value}
										value={option.value}
									>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						{DAY_FIELDS.map(({ key, label }) => (
							<div key={key} className="flex flex-col gap-1.5">
								<Label htmlFor={key}>{label}</Label>
								<Input
									id={key}
									type="number"
									min={0}
									max={MAX_DAYS}
									step={1}
									value={toInputValue(values[key])}
									disabled={!isOwner}
									onChange={(event) =>
										setDraft((current) => ({
											...current,
											[key]: parseAmount(
												event.target.value
											),
										}))
									}
									onBlur={() => handleDayBlur(key)}
									aria-label={label}
									data-testid={`mahnungswesen-${key}`}
								/>
							</div>
						))}
					</div>
					<div className="flex flex-col gap-1.5">
						<Label>
							Maximale Mahnstufe (inkl. Zahlungserinnerung)
						</Label>
						<Select
							value={String(values.mahnung_max_count)}
							onValueChange={(value) =>
								save({ mahnung_max_count: Number(value) })
							}
							disabled={!isOwner}
						>
							<SelectTrigger
								aria-label="Maximale Mahnstufe"
								data-testid="mahnungswesen-max-count"
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{Array.from(
									{ length: MAX_REMINDER_STAGE },
									(_, index) => (
										<SelectItem
											key={index + 1}
											value={String(index + 1)}
										>
											{index + 1} – bis{" "}
											{getReminderStageLabel(index + 1)}
										</SelectItem>
									)
								)}
							</SelectContent>
						</Select>
					</div>
				</div>

				<div className="flex flex-col gap-4">
					<h2 className="text-lg font-semibold">Gebühren</h2>
					<p
						className="text-sm text-gray-600"
						data-testid="mahnungswesen-fee-free-note"
					>
						Die Zahlungserinnerung ist immer gebührenfrei.
					</p>
					{Array.from(
						{ length: values.mahnung_max_count },
						(_, index) =>
							index + 1 >= FIRST_FEE_STAGE && (
								<div
									key={index}
									className="flex flex-col gap-1.5"
								>
									<Label
										htmlFor={`reminder-fee-${index + 1}`}
										data-testid={`mahnungswesen-fee-label-${
											index + 1
										}`}
									>
										Gebühr{" "}
										{getReminderStageLabel(index + 1)} (
										{currency})
									</Label>
									<Input
										id={`reminder-fee-${index + 1}`}
										type="number"
										min={0}
										step="0.05"
										value={toInputValue(values.fees[index])}
										disabled={!isOwner}
										onChange={(event) =>
											handleFeeChange(
												index,
												parseAmount(event.target.value)
											)
										}
										onBlur={handleFeeBlur}
										aria-label={`Gebühr ${getReminderStageLabel(
											index + 1
										)}`}
										data-testid={`mahnungswesen-fee-stage-${
											index + 1
										}`}
									/>
									{values.fees[index] > USUAL_MAX_FEE && (
										<p
											className="text-sm text-amber-700"
											data-testid={`mahnungswesen-fee-warning-${
												index + 1
											}`}
										>
											Deutlich über dem üblichen Rahmen –
											so hohe Gebühren lassen sich kaum
											durchsetzen.
										</p>
									)}
								</div>
							)
					)}
					<div
						role="note"
						data-testid="mahnungswesen-fee-legal-hint"
						className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
					>
						<Info className="mt-0.5 h-4 w-4 shrink-0" />
						<span>
							Mahngebühren dürfen Sie nur verlangen, wenn sie mit
							Betrag in Ihren AGB stehen. Die Höhe muss sich an
							den tatsächlich entstandenen Kosten (Porto und
							Aufwand) orientieren.{" "}
							{region === Region.DE
								? "Mahngebühren sind nicht umsatzsteuerpflichtig und werden ohne Mehrwertsteuer verrechnet."
								: "Die Beträge verstehen sich inklusive Mehrwertsteuer, der Satz richtet sich nach der Region des Kunden."}
						</span>
					</div>

					<h2 className="pt-2 text-lg font-semibold">Anzeige</h2>
					<div className="flex items-center justify-between gap-4">
						<Label htmlFor="reminder-sidebar-badge">
							Anzahl fälliger Mahnungen in der Seitenleiste bei
							Debitoren anzeigen
						</Label>
						<Switch
							id="reminder-sidebar-badge"
							checked={
								!!organization?.show_reminder_sidebar_badge
							}
							onCheckedChange={(checked) =>
								save({ show_reminder_sidebar_badge: checked })
							}
							disabled={!isOwner}
							aria-label="Anzahl fälliger Mahnungen in der Seitenleiste anzeigen"
							data-testid="mahnungswesen-sidebar-badge"
						/>
					</div>
				</div>

				<div className="flex flex-col gap-4">
					<h2 className="text-lg font-semibold">Ablauf</h2>
					<ol
						className="flex flex-col gap-3 rounded-md border bg-gray-50 p-4 text-sm"
						data-testid="mahnungswesen-timeline"
					>
						<li className="flex justify-between">
							<span>
								{dueBasis === "created_at"
									? "Rechnung erstellt"
									: "Rechnungsdatum"}
							</span>
							<span className="text-gray-500">Tag 0</span>
						</li>
						<li className="flex justify-between">
							<span>Zahlungsfrist abgelaufen</span>
							<span className="text-gray-500">
								Tag {timeline.dueDay}
							</span>
						</li>
						{timeline.stages.map((stage) => (
							<li
								key={stage.stage}
								className="flex justify-between"
								data-testid={`mahnungswesen-timeline-stage-${stage.stage}`}
							>
								<span>
									{stage.label}
									{stage.fee > 0 && (
										<span className="text-gray-500">
											{" "}
											· Gebühr{" "}
											{formatCurrency(
												stage.fee,
												currency
											)}
										</span>
									)}
								</span>
								<span className="text-gray-500">
									Tag {stage.day}
								</span>
							</li>
						))}
					</ol>
					<p className="text-sm text-gray-600">
						Titel, Brieftext, Briefkopf und Fusszeile der
						Zahlungserinnerung und Mahnungen passen Sie in der{" "}
						<Link
							to="/organisation/pdf-gestaltung"
							className="underline"
							data-testid="mahnungswesen-pdf-config-link"
						>
							PDF-Gestaltung
						</Link>{" "}
						an.
					</p>
				</div>
			</section>
		</div>
	);
};
